"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import type { CSSProperties, RefObject } from "react";
import { usePlaceSearch } from "@ishvaram/ui/hooks";
import type { PlaceResult } from "@ishvaram/ui/hooks";
import { searchPlaces } from "@/lib/api/misc";
import { isPlaceResult } from "@/lib/place-result";
import { resolvePlaceQuery } from "@/lib/place-gazetteer-query";
import { formatPlaceOptionLabel } from "./PlaceAutocomplete.option";
import { normalizePlaceDisplay, resolveExactPlaceInput } from "./PlaceAutocomplete.resolve";
import {
  dedupeSamePlaces,
  exactMatchesAlone,
  placeCoordinateLabel,
  sharedPlaceLabels,
} from "./PlaceAutocomplete.rank";

interface PlaceCityData {
  name: string;
  state: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

interface PlaceAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (place: PlaceResult) => void;
  id?: string;
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
  /**
   * Told when a search for a 4+ character query has settled with no rows at
   * all (and back to false when that stops being true), so the host can swap
   * its hint for a correction path. Never fires mid-debounce.
   */
  onNoMatchChange?: (noMatch: boolean) => void;
}

const NO_MATCH_MIN_CHARS = 4;

interface PlaceAutocompleteDropdownProps {
  id: string;
  listRef: RefObject<HTMLUListElement | null>;
  results: PlaceResult[];
  activeIndex: number;
  onPick: (place: PlaceResult, index: number) => void;
  setActiveIndex: (index: number) => void;
}

/**
 * A row that appeared under a resting finger is not a choice. Rows appended
 * this recently (API extras landing after the local rows) ignore a press; the
 * input's blur then exact-commits what was typed instead (C07).
 */
const LATE_ROW_GUARD_MS = 400;

const DROPDOWN_STACKING_LAYER = "var(--z-index-dropdown)";
const CLOSED_COMBOBOX_STYLE: CSSProperties = { position: "relative" };
const OPEN_COMBOBOX_STYLE: CSSProperties = {
  position: "relative",
  zIndex: DROPDOWN_STACKING_LAYER,
};

function comboboxStackingStyle(isOpen: boolean): CSSProperties {
  // Raise the whole open combobox, not only its absolutely positioned list.
  // Otherwise later hero/sticky siblings can intercept taps on visible options.
  return isOpen ? OPEN_COMBOBOX_STYLE : CLOSED_COMBOBOX_STYLE;
}

// 3,776 Indian cities (population ≥ 15k) live in /public/data/place-cities.json (~400KB).
// Loaded on first focus instead of bundled — saves ~400KB from initial JS payload
// for every visitor who doesn't open the autocomplete.
// Pre-lowercased once at load so the per-keystroke filter allocates nothing
// (INP fix 2026-07-22: filterLocal scanned all 3,776 rows calling toLowerCase()
// on name + display on every keypress in the birth-place field).
type IndexedCity = PlaceResult & { _nameLc: string; _displayLc: string };

let _citiesPromise: Promise<IndexedCity[]> | null = null;
function loadLocalCities(): Promise<IndexedCity[]> {
  if (_citiesPromise) return _citiesPromise;
  _citiesPromise = fetch("/data/place-cities.json")
    .then((r) => (r.ok ? (r.json() as Promise<PlaceCityData[]>) : []))
    .then((rows: PlaceCityData[]): IndexedCity[] =>
      rows.map((c, i) => {
        const display = `${c.name}, ${c.state}, India`;
        return {
          geoname_id: -(i + 1), // negative = local, never clash with GeoNames
          name: c.name,
          display,
          latitude: c.latitude,
          longitude: c.longitude,
          timezone: c.timezone,
          country: "IN",
          population: 0,
          _nameLc: c.name.toLowerCase(),
          _displayLc: display.toLowerCase(),
        };
      })
    )
    .catch(() => []);
  return _citiesPromise;
}

/** Lazy-load the 3,776-city local index on first focus (cached after first hit). */
function useLocalCityIndex(focused: boolean): { cities: IndexedCity[]; ready: boolean } {
  const [localCities, setLocalCities] = useState<IndexedCity[]>([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!focused || localCities.length > 0) return;
    let cancelled = false;
    loadLocalCities().then((rows) => {
      if (cancelled) return;
      setLocalCities(rows);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [focused, localCities.length]);
  return { cities: localCities, ready };
}

function filterLocal(cities: IndexedCity[], query: string): PlaceResult[] {
  if (!query.trim()) return cities.slice(0, 8);
  const q = normalizePlaceDisplay(query);
  // Prefix matches first (array is population-sorted), then substring matches
  const prefix: PlaceResult[] = [];
  const substr: PlaceResult[] = [];
  for (const c of cities) {
    if (c._nameLc.startsWith(q)) {
      prefix.push(c);
      if (prefix.length === 8) break;
    } else if (c._displayLc.includes(q)) {
      substr.push(c);
    }
  }
  return [...prefix, ...substr].slice(0, 8);
}

function mergePlaceResults(
  value: string,
  localMatches: PlaceResult[],
  safeApiResults: PlaceResult[]
) {
  if (value.trim().length < 4) return localMatches;
  const apiExtra = safeApiResults.filter((apiPlace) => {
    const apiKey = normalizePlaceDisplay(apiPlace.display);
    return !localMatches.some((localPlace) => {
      if (normalizePlaceDisplay(localPlace.display) === apiKey) return true;
      return (
        localPlace.name.toLowerCase() === apiPlace.name.toLowerCase() &&
        Math.abs(localPlace.latitude - apiPlace.latitude) < 0.01 &&
        Math.abs(localPlace.longitude - apiPlace.longitude) < 0.01
      );
    });
  });
  return [...localMatches, ...apiExtra];
}

export default function PlaceAutocomplete({
  value,
  onChange,
  onSelect,
  id = "birthPlace",
  placeholder = "Start typing a city (e.g. Mumbai, Delhi, Karnal)",
  required = true,
  invalid = false,
  describedBy,
  className = "input-sacred w-full rounded-lg px-4 py-3 pr-9 text-body",
  onNoMatchChange,
}: PlaceAutocompleteProps) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const localIndex = useLocalCityIndex(focused);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setIsHydrated(true), 0);
    return () => window.clearTimeout(id);
  }, []);

  // The gazetteer-searchable form of what the user typed, applied before ALL
  // matching below — dropdown filter, API search, and blur/click-outside
  // exact-commit. A Devanagari big city ("मुंबई") becomes its English name
  // (the /hi form-completion fix), and a bare old name ("Calcutta", "Baroda")
  // becomes the name the gazetteer uses, so a small exact namesake can never
  // stand alone in its place. Other input passes through unchanged.
  const placeQuery = useMemo(() => resolvePlaceQuery(value), [value]);
  const gazetteerQuery = placeQuery.query;

  const { results, localMatches, isFetching, settledQuery } = usePlaceOptions(
    gazetteerQuery,
    localIndex.cities,
    placeQuery.namesake
  );
  useNoMatchSignal(
    {
      ready: localIndex.ready,
      isFetching,
      rowCount: results.length,
      settledQuery,
      query: gazetteerQuery,
    },
    onNoMatchChange
  );
  const lateRows = useLateRowGuard(results.length);

  const isOpen = focused && results.length > 0;
  // Show spinner when API is in flight and no local matches surfaced yet
  const isSearching =
    focused && gazetteerQuery.trim().length >= 4 && isFetching && localMatches.length === 0;
  const activeOptionId = activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined;

  useEffect(() => {
    startTransition(() => setActiveIndex(-1));
  }, [results.length]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    listRef.current
      .querySelector<HTMLLIElement>(`[data-option-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleSelect = useCallback(
    (place: PlaceResult | undefined) => {
      if (!isPlaceResult(place)) return;
      onChange(place.display);
      onSelect(place);
      setFocused(false);
    },
    [onChange, onSelect]
  );

  const pickOption = useCallback(
    (place: PlaceResult, index: number) => {
      if (lateRows.isLate(index)) return;
      handleSelect(place);
    },
    [handleSelect, lateRows]
  );

  const commitExactMatchOrClose = useCallback(() => {
    // Compare on the gazetteer form so a typed Devanagari city ("मुंबई")
    // auto-commits its canonical row exactly like typed "Mumbai" does.
    const exact = resolveExactPlaceInput(gazetteerQuery, results);
    if (exact) {
      handleSelect(exact);
      return;
    }
    setFocused(false);
  }, [handleSelect, results, gazetteerQuery]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        commitExactMatchOrClose();
      }
    }
    // Close after the outside control receives its click. Committing an exact
    // place on mousedown can replace the combobox subtree before an adjacent
    // Continue button receives click, dropping the seeker's first tap.
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [commitExactMatchOrClose]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  }

  return (
    <div ref={containerRef} style={comboboxStackingStyle(isOpen)}>
      <input
        id={id}
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => window.setTimeout(commitExactMatchOrClose, 0)}
        placeholder={placeholder}
        className={className}
        style={{ color: "var(--ish-dark)" }}
        disabled={!isHydrated}
        autoComplete="off"
        autoCapitalize="words"
        spellCheck={false}
        role="combobox"
        data-hydrated={isHydrated ? "true" : "false"}
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        aria-required={required || undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />

      {isSearching && <SearchSpinner />}
      {isOpen && (
        <PlaceAutocompleteDropdown
          id={id}
          listRef={listRef}
          results={results}
          activeIndex={activeIndex}
          onPick={pickOption}
          setActiveIndex={setActiveIndex}
        />
      )}
    </div>
  );
}

/**
 * Records when an OPEN option list last grew and which rows were the new ones.
 * Opening the list (0 -> n) is not guarded, so row 1 is always tappable; only
 * rows appended under an already-visible list are.
 */
function useLateRowGuard(rowCount: number) {
  const previousCount = useRef(rowCount);
  const growth = useRef({ at: Number.NEGATIVE_INFINITY, firstNewRow: Number.POSITIVE_INFINITY });
  useLayoutEffect(() => {
    if (previousCount.current > 0 && rowCount > previousCount.current) {
      growth.current = { at: performance.now(), firstNewRow: previousCount.current };
    }
    previousCount.current = rowCount;
  }, [rowCount]);
  return useMemo(
    () => ({
      isLate: (index: number) =>
        index >= growth.current.firstNewRow &&
        performance.now() - growth.current.at < LATE_ROW_GUARD_MS,
    }),
    []
  );
}

/**
 * The option rows for the gazetteer form of the typed text: local index first,
 * then /_places extras when the local index cannot settle the query.
 */
function usePlaceOptions(
  gazetteerQuery: string,
  localCities: IndexedCity[],
  legacyNamesake: string | null
) {
  // Local matches for the current query — used as fallback below minChars
  // and as the prefix layer in merged results above minChars.
  const localMatches = useMemo(
    () => filterLocal(localCities, gazetteerQuery),
    [localCities, gazetteerQuery]
  );

  // Skip the /_places API entirely when the local 3,776-city index already
  // has enough matches — most users type a major city the local index fully
  // covers, so the API call (for rare small towns) is pure waste. Only when
  // local has < LOCAL_SUFFICIENT matches do we wake the Worker. This is the
  // main lever cutting the ~2.2M/mo /_places Worker invocations; local results
  // still render instantly, so there is no perceptible UX change.
  //
  // Also skip it when the typed text already exactly names a local city: the
  // seeker is done, and API extras arriving 0.5-3 s later used to land row 2
  // exactly under Submit's centre (New Delhi -> Patna, Kanpur -> Kanchanaburi
  // TH, live 2026-10-07). When the local index loads after an API reply, the
  // gate flips and the hook clears those extras.
  const LOCAL_SUFFICIENT = 5;
  const shouldFetch = useCallback(
    () =>
      localMatches.length < LOCAL_SUFFICIENT &&
      !resolveExactPlaceInput(gazetteerQuery, localMatches),
    [localMatches, gazetteerQuery]
  );

  // Pass localMatches as fallbackResults so the hook returns them when
  // query.length < minChars (empty field → top-8 cities, 1-3 chars → filtered).
  // At/above minChars the hook fetches from the API and returns apiResults.
  // minChars=4 + debounceMs=350 keeps short queries on the local 3,776-city
  // index (which covers every Indian city ≥15k pop) and only hits the API
  // when the user is searching for a small town the local index doesn't have.
  // The hook also caches each query's results in-memory, so backspacing or
  // re-typing a prefix never re-fires the network call.
  const { apiResults, isFetching, settledQuery } = usePlaceSearch({
    query: gazetteerQuery,
    searchFn: searchPlaces,
    debounceMs: 350,
    minChars: 4,
    fallbackResults: localMatches,
    shouldFetch,
  });
  const safeApiResults = apiResults.filter(isPlaceResult);

  // Four branches:
  //   empty field   → top-8 local cities
  //   1-3 chars     → filtered local cities (no API hit — local index covers
  //                   every Indian city ≥15k pop)
  //   ≥4 chars      → local matches + API extras (deduped) for rare-town reach,
  //                   and only when local matches are insufficient (shouldFetch)
  // An exact name match stands alone (no junk row for a stray tap), and rows
  // repeating a label at the same spot collapse. A local town that literally
  // carries the typed old name (Baroda MP under Vadodara) stays listed second.
  const namesakes = useMemo(
    () => (legacyNamesake ? localCities.filter((city) => city._nameLc === legacyNamesake) : []),
    [localCities, legacyNamesake]
  );
  const results: PlaceResult[] = dedupeSamePlaces([
    ...exactMatchesAlone(
      gazetteerQuery,
      mergePlaceResults(gazetteerQuery, localMatches, safeApiResults)
    ),
    ...namesakes,
  ]);
  return { results, localMatches, isFetching, settledQuery };
}

/**
 * Report no-match transitions to the host (effect, so never during render): a
 * 4+ character query whose search has settled (local index loaded, no request
 * in flight, the hook caught up with the debounce) with no rows at all.
 */
function useNoMatchSignal(
  search: {
    ready: boolean;
    isFetching: boolean;
    rowCount: number;
    settledQuery: string;
    query: string;
  },
  onNoMatchChange?: (noMatch: boolean) => void
) {
  const noMatch =
    search.ready &&
    !search.isFetching &&
    search.rowCount === 0 &&
    search.settledQuery === search.query &&
    search.query.trim().length >= NO_MATCH_MIN_CHARS;
  useEffect(() => {
    onNoMatchChange?.(noMatch);
  }, [noMatch, onNoMatchChange]);
}

function SearchSpinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: "50%",
        right: 12,
        transform: "translateY(-50%)",
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: "2px solid rgba(0,0,0,0.12)",
        borderTopColor: "var(--ish-accent, var(--color-accent))",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}

function PlaceAutocompleteDropdown({
  id,
  listRef,
  results,
  activeIndex,
  onPick,
  setActiveIndex,
}: PlaceAutocompleteDropdownProps) {
  const sharedLabels = sharedPlaceLabels(results);
  return (
    <ul
      ref={listRef}
      id={`${id}-listbox`}
      role="listbox"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: DROPDOWN_STACKING_LAYER,
        maxHeight: "240px",
        overflowY: "auto",
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "0 0 8px 8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        margin: 0,
        padding: 0,
        listStyle: "none",
      }}
    >
      {results.map((place, index) => (
        <PlaceOption
          key={place.geoname_id}
          id={`${id}-option-${index}`}
          place={place}
          index={index}
          isActive={index === activeIndex}
          isLast={index === results.length - 1}
          disambiguator={
            sharedLabels.has(normalizePlaceDisplay(place.display))
              ? placeCoordinateLabel(place)
              : undefined
          }
          onPick={onPick}
          setActiveIndex={setActiveIndex}
        />
      ))}
    </ul>
  );
}

function PlaceOption({
  id,
  place,
  index,
  isActive,
  isLast,
  disambiguator,
  onPick,
  setActiveIndex,
}: {
  id: string;
  place: PlaceResult;
  index: number;
  isActive: boolean;
  isLast: boolean;
  disambiguator?: string;
  onPick: (place: PlaceResult, index: number) => void;
  setActiveIndex: (index: number) => void;
}) {
  const isLocal = place.geoname_id < 0;
  const { cityName, region, label } = formatPlaceOptionLabel(place.display, disambiguator);
  return (
    <li
      id={id}
      data-option-index={index}
      role="option"
      aria-selected={isActive}
      aria-label={label}
      onMouseDown={() => onPick(place, index)}
      onMouseEnter={() => setActiveIndex(index)}
      // A 44 px row (design policy): 10 px padding around one 14 px line made
      // it 42 px, so a thumb aimed between two cities could pick the wrong one.
      // The list is position:absolute, so a taller row moves nothing below it.
      // The name is body size, the same as the input it completes.
      className={`flex min-h-11 cursor-pointer items-center gap-1.5 px-3.5 py-2.5 text-body text-text-primary ${
        isActive ? "bg-surface-brand-subtle" : "bg-transparent"
      } ${isLast ? "" : "border-b border-border-subtle"}`}
    >
      {isLocal && <span className="shrink-0 text-caption text-[var(--intent-brand-text)]">★</span>}
      <span>
        <span className="font-medium">{cityName}</span>
        {region && <span className="ml-1.5 text-caption text-text-muted">{`, ${region}`}</span>}
      </span>
    </li>
  );
}
